import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export type AppRole = 'master' | 'admin' | 'moderator' | 'user';

interface AdminData {
  role: AppRole;
  roles: AppRole[];
  isMaster: boolean;
  isAdmin: boolean;
  isModerator: boolean;
}

const defaultData: AdminData = {
  role: 'user',
  roles: [],
  isMaster: false,
  isAdmin: false,
  isModerator: false,
};

async function fetchUserRole(): Promise<AdminData> {
  const { data, error } = await supabase.functions.invoke('get-user-role');

  if (error) {
    console.error('Error fetching user role:', error);
    throw error;
  }

  return {
    role: data.role || 'user',
    roles: data.roles || [],
    isMaster: data.isMaster || false,
    isAdmin: data.isAdmin || false,
    isModerator: data.isModerator || false,
  };
}

export function useAdmin() {
  const { user } = useAuth();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['user-role', user?.id],
    queryFn: fetchUserRole,
    enabled: !!user,
    staleTime: 10 * 60 * 1000, // 10 minutes - role rarely changes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });

  return {
    role: data?.role ?? defaultData.role,
    roles: data?.roles ?? defaultData.roles,
    isMaster: data?.isMaster ?? defaultData.isMaster,
    isAdmin: data?.isAdmin ?? defaultData.isAdmin,
    isModerator: data?.isModerator ?? defaultData.isModerator,
    isLoading: isLoading && !data, // Only loading on initial fetch, not background refetch
    error: error?.message ?? null,
    refetch,
  };
}
